import type { EntityGroup, GrantExpirySummary } from './entityGroups'
import { summariseGrantExpiry } from './entityGroups'

interface WhatYouCanSeeProps {
  group: EntityGroup
  /**
   * Optional ONLY so a caller with a better source can override it; left out,
   * this panel computes the row itself from the group's own grant rows rather
   * than falling back to a constant. It used to default to "No grants
   * expiring" with nothing having looked, which is an assertion no data
   * supported.
   */
  grantsExpiring?: GrantExpirySummary
  extendedVisibility: boolean
  /** Who turned extended visibility on — always the inventor, never the portfolio. */
  extendedVisibilityGrantedBy?: string
  /** The portfolio reading this screen, for "turned this on for <portfolio>". */
  portfolioName?: string
  onSwitchOff?: () => void
}

/**
 * Figure S5 prints "1 on 30 Nov 2026". The three states below are three
 * different facts and the row says which one it is holding — never "No grants
 * expiring" for data it has not read.
 */
export function grantExpiryText(summary: GrantExpirySummary): string {
  if (!summary.tracked) return 'Not tracked'
  if (summary.count === 0) return 'No grants expiring'
  const when = summary.earliest
    ? new Date(summary.earliest).toLocaleDateString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : null
  return when ? `${summary.count} on ${when}` : `${summary.count} expiring`
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-iflow-900/5 py-2 first:border-t-0">
      <span className="text-[12.5px] text-gray-500">{label}</span>
      <span className="text-right text-[12.5px] font-semibold text-iflow-900">{children}</span>
    </div>
  )
}

/**
 * Figure S5's "What you can see" card — how much of this entity the portfolio
 * is being shown, and who decided it.
 *
 * Extended visibility can only be turned OFF from here. It is the inventor's
 * switch alone: a portfolio can turn it off, never on, and this panel never
 * offers a way to widen anything.
 */
export default function WhatYouCanSee({
  group,
  grantsExpiring,
  extendedVisibility,
  extendedVisibilityGrantedBy,
  portfolioName,
  onSwitchOff,
}: WhatYouCanSeeProps) {
  const projectCount = group.projects.length
  const expiry = grantsExpiring ?? summariseGrantExpiry(group.projects)

  return (
    <div data-part="WhatYouCanSee" className="light-card p-4">
      <h3 className="text-[15px] font-bold text-iflow-900">👁️ What you can see</h3>

      {projectCount === 0 ? (
        <p className="mt-2 text-[12.5px] text-gray-600">
          Nothing is shared with you yet. Being in your portfolio grants nothing by itself — the
          inventor decides, project by project.
        </p>
      ) : (
        <div className="mt-2">
          <Row label="Projects granted">
            {projectCount} project{projectCount === 1 ? '' : 's'} granted
          </Row>
          <Row label="Grants expire">{grantExpiryText(expiry)}</Row>
          <Row label="Extended visibility">
            <span className="flex items-center justify-end gap-2">
              <span
                aria-hidden
                className={`inline-block h-4 w-8 rounded-full ${extendedVisibility ? 'bg-iflow-500' : 'bg-gray-300'}`}
              >
                <span
                  className={`mt-0.5 block h-3 w-3 rounded-full bg-white ${extendedVisibility ? 'ml-4' : 'ml-1'}`}
                />
              </span>
              {extendedVisibility ? 'on' : 'off'}
            </span>
          </Row>
        </div>
      )}

      {projectCount > 0 && (
        <>
          <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500">
            {extendedVisibility ? (
              <>
                {extendedVisibilityGrantedBy ?? 'The inventor'} turned this on
                {portfolioName ? ` for ${portfolioName}` : ''}. You can switch it off, never on.
              </>
            ) : (
              <>Extended visibility is off. Only the inventor can turn it on.</>
            )}
          </p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[11.5px] text-gray-500">Only the inventor can widen this.</span>
            {extendedVisibility && (
              <button
                type="button"
                onClick={onSwitchOff}
                title="A narrowing of your own view · audited EXTENDED_VISIBILITY_DISABLED"
                className="shrink-0 rounded-lg border border-iflow-900/15 bg-white px-2.5 py-1 text-[12px] font-semibold text-iflow-800"
              >
                Switch off
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
